/**
 * O motivo obrigatorio por operacao, do lado da tela (06/10/2026).
 *
 * O servidor recusa as operacoes que tiram algo de alguem — desativar pessoa,
 * papel ou cadastro, tirar papel, tirar permissao, tirar alcada, encerrar
 * sessoes — sem um motivo escrito de pelo menos `MOTIVO_MINIMO` letras
 * (`backend/src/http/motivo.ts`). A tela pede o motivo na propria confirmacao,
 * com o mesmo exemplo que a recusa do servidor daria, e nao deixa confirmar
 * antes do piso: a ida ao servidor que ele recusaria e uma volta inutil.
 *
 * As chaves sao as do servidor; `motivo.test.ts` confere que as duas listas
 * nao se separam.
 */
export const MOTIVO_MINIMO = 10;
export const MOTIVO_MAXIMO = 240;

export const EXEMPLO_DO_MOTIVO = Object.freeze({
  'pessoa.desativar': 'saiu da empresa em 05/10/2026',
  'pessoa.encerrar_sessoes': 'celular perdido na obra',
  'pessoa.tirar_papel': 'mudou para a manutenção',
  'papel.desativar': 'substituído pelo papel de gestor da filial',
  'papel.tirar_permissao': 'a compra passou para o almoxarifado',
  'alcada.tirar': 'o limite da compra foi revisto pela diretoria',
  'cadastro.desativar': 'obra encerrada em 30/09/2026',
  'segundo_fator.desativar': 'troquei de celular e vou ativar de novo',
  'valor_hora.definir': 'reajuste do acordo coletivo de 2026',
  'funcao.trocar': 'promovido a operador de escavadeira em 01/10/2026',
  'depreciacao.definir': 'nota fiscal de compra de 03/2024 e vida útil da política de frota',
  'rateio.trocar': 'a diretoria aprovou o rateio pelo valor de aquisição',
  'classificacao.trocar': 'a contabilidade separou o seguro dos indiretos',
  'conta_gerencial.desativar': 'juntada à conta de peças pela contabilidade',
  'orcamento.descartar': 'a diretoria pediu refazer com a obra nova',
  'estudo.arquivar': 'a máquina foi vendida antes da conclusão',
  'criterio.retirar': 'substituído pelo critério de risco com peso novo',
  'papel.travar_abastecimento': 'o comboio passa a lançar só do próprio tanque',
  'papel.definir_inicio': 'o comboista passa a abrir o app no histórico de abastecimentos',
  'papel.duplicar': 'papel do Posto Central, a partir do posto parceiro',
  'papel.fixar_fornecedor': 'o papel passa a valer só para o Posto Central',
} as const);

export type OperacaoComMotivo = keyof typeof EXEMPLO_DO_MOTIVO;

/** A ajuda do campo: o que escrever, com o exemplo e o piso. */
export function ajudaDoMotivo(operacao: OperacaoComMotivo): string {
  return `Fica na trilha com o seu nome. Por exemplo: "${EXEMPLO_DO_MOTIVO[operacao]}". Pelo menos ${String(MOTIVO_MINIMO)} caracteres.`;
}

/** O motivo cumpre o piso? */
export function motivoValido(motivo: string): boolean {
  const limpo = motivo.trim();
  return limpo.length >= MOTIVO_MINIMO && limpo.length <= MOTIVO_MAXIMO;
}

/** O que falta, para o botao desligado dizer por que. Nulo quando cumpre. */
export function faltaNoMotivo(motivo: string): string | null {
  const n = motivo.trim().length;
  if (n === 0) return 'Escreva o motivo para confirmar.';
  if (n < MOTIVO_MINIMO) return `Escreva mais ${String(MOTIVO_MINIMO - n)} ${MOTIVO_MINIMO - n === 1 ? 'caractere' : 'caracteres'} no motivo.`;
  if (n > MOTIVO_MAXIMO) return `O motivo passou de ${String(MOTIVO_MAXIMO)} caracteres. Encurte.`;
  return null;
}
